/**
 * AiComposer — the practitioner input bar at the bottom of the chat.
 *
 * - Disabled (visually and functionally) when limitReached === true or
 *   isLoading === true.
 * - Sends on Enter (without Shift) for keyboard efficiency, or on Send button click.
 * - Clears input after submission.
 * - Shows a disabled tooltip explaining WHY when the limit is reached.
 *
 * No form library needed here — the composer is a single uncontrolled input
 * with a local string state, not a Formik form.
 */

import React, { useState, useRef, KeyboardEvent } from "react";
import { Box, CircularProgress, IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import { SendOutlined } from "@mui/icons-material";
import { AI_PROMPT_LIMIT } from "./assessmentAi.mock";

type AiComposerProps = {
    limitReached: boolean;
    isLoading: boolean;
    promptsUsed: number;
    onSend: (text: string) => void;
};

export const AiComposer = ({ limitReached, isLoading, promptsUsed, onSend }: AiComposerProps): JSX.Element => {
    const [value, setValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const isDisabled = limitReached || isLoading;
    const remaining = AI_PROMPT_LIMIT - promptsUsed;

    const handleSend = () => {
        const trimmed = value.trim();
        if (!trimmed || isDisabled) {
            return;
        }
        onSend(trimmed);
        setValue("");
        // Return focus to the input after sending (only while still enabled)
        if (!limitReached) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const placeholderText = limitReached
        ? "Session limit reached — no further prompts available"
        : `Ask about this assessment… (${remaining} prompt${remaining === 1 ? "" : "s"} remaining)`;

    return (
        <Box display="flex" gap={1} alignItems="flex-end">
            <Tooltip
                title={limitReached ? `You have used all ${AI_PROMPT_LIMIT} prompts for this session.` : ""}
                placement="top"
            >
                {/* Tooltip requires a non-disabled wrapper when the child is disabled */}
                <span style={{ flex: 1 }}>
                    <TextField
                        inputRef={inputRef}
                        fullWidth
                        multiline
                        maxRows={4}
                        variant="outlined"
                        size="small"
                        disabled={isDisabled}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholderText}
                        aria-label="Ask AI about this assessment"
                        InputProps={{
                            endAdornment: isLoading ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={18} color="primary" />
                                </InputAdornment>
                            ) : undefined,
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                bgcolor: isDisabled ? "action.disabledBackground" : "background.paper",
                            },
                        }}
                    />
                </span>
            </Tooltip>

            <Tooltip title={limitReached ? "Session limit reached" : isLoading ? "Waiting for AI…" : "Send (Enter)"}>
                <span>
                    <IconButton
                        color="primary"
                        onClick={handleSend}
                        disabled={isDisabled || value.trim().length === 0}
                        aria-label="Send prompt"
                        sx={{
                            bgcolor: "primary.main",
                            color: "common.white",
                            "&:hover": { bgcolor: "primary.dark" },
                            "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
                        }}
                    >
                        <SendOutlined fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
};
